import type { RemediationRule, Issue, FixResult } from '../index.js';

export type RGB = [number, number, number];

export function parseColor(c: string): RGB | null {
  const m = c.match(/rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

export function relLuminance([r, g, b]: RGB): number {
  const s = [r, g, b].map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * s[0]! + 0.7152 * s[1]! + 0.0722 * s[2]!;
}

export function contrastRatio(a: RGB, b: RGB): number {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

function mix(a: RGB, b: RGB, k: number): RGB {
  return [0, 1, 2].map((i) => Math.round(a[i]! + (b[i]! - a[i]!) * k)) as RGB;
}

/** The nearest colour to `fg` — same hue, only darker or lighter — that reaches
 *  `target` on `bg`. On a mid-grey background where nothing reaches it, the
 *  better of black/white. */
export function passingColor(fg: RGB, bg: RGB, target: number): RGB {
  const black: RGB = [0, 0, 0];
  const white: RGB = [255, 255, 255];
  const toward = contrastRatio(black, bg) >= contrastRatio(white, bg) ? black : white;
  if (contrastRatio(toward, bg) < target) return toward;
  let lo = 0, hi = 1;
  for (let i = 0; i < 12; i++) {
    const mid = (lo + hi) / 2;
    if (contrastRatio(mix(fg, toward, mid), bg) >= target) hi = mid; else lo = mid;
  }
  return mix(fg, toward, hi);
}

export function isLargeText(fontSizePx: number, fontWeight: number): boolean {
  return fontSizePx >= 24 || (fontSizePx >= 18.66 && fontWeight >= 700);
}

export type RGBA = [number, number, number, number];

export function parseRGBA(c: string): RGBA | null {
  const m = c.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?/);
  if (!m) return null;
  let a = m[4] === undefined ? 1 : parseFloat(m[4]);
  if (m[4]?.endsWith('%')) a /= 100;
  return [Number(m[1]), Number(m[2]), Number(m[3]), Math.max(0, Math.min(1, a))];
}

/** `top` painted over an opaque `under` (source-over). */
function over(top: RGBA, under: RGB): RGB {
  const a = top[3];
  return [0, 1, 2].map((i) => Math.round(top[i]! * a + under[i]! * (1 - a))) as RGB;
}

/**
 * The colour actually painted behind `el`, compositing semi-transparent
 * backgrounds up the tree. Returns null when it can't be known from CSS
 * colours alone — a background image/gradient somewhere below the first
 * opaque layer — so the caller skips instead of guessing (guessing produced
 * "Contrast 1.00" false positives: white text on rgba(255,255,255,.1) over a
 * dark header, or on a hero photo).
 */
export function effectiveBg(el: HTMLElement): RGB | null {
  const layers: RGBA[] = [];
  let cur: HTMLElement | null = el;
  while (cur) {
    const cs = window.getComputedStyle(cur);
    if (cs.backgroundImage && cs.backgroundImage !== 'none') return null;
    const bg = parseRGBA(cs.backgroundColor);
    if (bg && bg[3] > 0) {
      layers.push(bg);
      if (bg[3] >= 0.999) break;
    }
    cur = cur.parentElement;
  }
  // Composite from the bottom (page canvas = white) up to the element.
  let base: RGB = [255, 255, 255];
  for (let i = layers.length - 1; i >= 0; i--) base = over(layers[i]!, base);
  return base;
}

// Per-element "hidden by itself or an ancestor" cache: a page with thousands of
// text nodes would otherwise re-walk (and re-style) the same ancestors each time.
let hiddenCache = new WeakMap<Element, boolean>();
export function resetContrastCache(): void { hiddenCache = new WeakMap(); }

function hiddenSelfOrAncestor(el: HTMLElement | null): boolean {
  if (!el) return false;
  const cached = hiddenCache.get(el);
  if (cached !== undefined) return cached;
  const cs = window.getComputedStyle(el);
  const hidden = cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.1
    || hiddenSelfOrAncestor(el.parentElement);
  hiddenCache.set(el, hidden);
  return hidden;
}

/** Rendered and visible to a sighted user (not display:none, hidden, faded out or zero-size). */
function isVisible(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect?.();
  if (r && (r.width === 0 || r.height === 0)) return false;
  return !hiddenSelfOrAncestor(el);
}

/** True when the element itself holds readable text (not only inside child
 *  elements). Separators like "›", "/", "|", "•" — no letter or digit — are
 *  decoration, which WCAG 1.4.3 exempts. */
function hasOwnText(el: HTMLElement): boolean {
  for (const n of Array.from(el.childNodes)) {
    if (n.nodeType === 3 && /[\p{L}\p{N}]/u.test(n.textContent || '')) return true;
  }
  return false;
}

/**
 * Contrast of the text an element itself renders, or null when it can't be
 * measured reliably (no own text, invisible, transparent/gradient text, or a
 * background image). Shared by the AA (1.4.3) and AAA (1.4.6) rules.
 */
export function measureTextContrast(el: HTMLElement): { ratio: number; px: number; weight: number } | null {
  if (!hasOwnText(el) || !isVisible(el)) return null;
  const style = window.getComputedStyle(el);
  const fgA = parseRGBA(style.color);
  if (!fgA || fgA[3] < 0.1) return null; // e.g. gradient text (color: transparent + background-clip)
  const bg = effectiveBg(el);
  if (!bg) return null;
  const fg = fgA[3] < 0.999 ? over(fgA, bg) : ([fgA[0], fgA[1], fgA[2]] as RGB);
  return {
    ratio: contrastRatio(fg, bg),
    px: parseFloat(style.fontSize),
    weight: parseInt(style.fontWeight, 10) || 400,
  };
}

export const contrastRule: RemediationRule = {
  id: 'contrast',
  wcag: ['1.4.3', '1.4.6'],
  tier: 'AA',
  needsLLM: false,

  detect(root) {
    if (typeof window === 'undefined' || !('getComputedStyle' in window)) return [];
    const issues: Issue[] = [];
    resetContrastCache(); // styles may have changed since the last run
    const nodes = root.querySelectorAll<HTMLElement>(
      'p, span, a, li, h1, h2, h3, h4, h5, h6, button, label, td, th, dt, dd',
    );
    nodes.forEach((node, i) => {
      const m = measureTextContrast(node);
      if (!m) return;
      const { ratio, px, weight } = m;
      const target = isLargeText(px, weight) ? 3 : 4.5;
      if (ratio < target) {
        issues.push({
          ruleId: 'contrast',
          wcag: ['1.4.3'],
          selector: `${node.tagName.toLowerCase()}:nth-of-type(${i + 1})`,
          element: node,
          severity: ratio < target * 0.7 ? 'serious' : 'moderate',
          message: `Contrast ${ratio.toFixed(2)} below ${target}`,
          payload: { ratio, target },
        });
      }
    });
    return issues;
  },

  async fix(issue): Promise<FixResult> {
    const el = issue.element as HTMLElement | undefined;
    if (!el) return { ok: false };
    const bg = effectiveBg(el);
    const fg = parseColor(window.getComputedStyle(el).color);
    if (!bg || !fg) return { ok: false };
    const target = (issue.payload as { target?: number } | undefined)?.target === 3 ? 3 : 4.5;
    const c = passingColor(fg, bg, target + 0.1);
    el.style.setProperty('color', `rgb(${c[0]}, ${c[1]}, ${c[2]})`, 'important');
    return { ok: true };
  },
};
